import { DetailsIssuedAlert } from '@/entities';
import { CalendarSinglePicker, CustomInput, useAppSelector, CustomSelect, SGD_OUT_COUNT_TOO_LARGE } from '@/shared';
import styles from '@/shared/components/custom-select/custom-select.module.scss';
import {
	AssemblySgdOutCreateRequest,
	BodyUpdateAssemblyTechMap,
	useUpdateAssemblyTechMapMutation,
} from '@/shared/state/api/swagger';
import { selectCurrentUserId } from '@/shared/state/slices';
import { useOperationNotification } from '@/widgets/operation/hooks';
import { IssueDetailsFormAssemblyProps } from '@/widgets/operation/model/operation.types';
import { formatDate } from '@/widgets/operation/utils';
import { Button, chakra } from '@chakra-ui-kraud/react';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { issueDetailsSchema } from '../../../model/operations.schema';
import { OperationLayout } from '../../operation-layout/operation-layout';
import { OperationFormHeader } from '../form-header/form-header';

dayjs.extend(utc);
dayjs.extend(timezone);

type AssemblyFormValues = Omit<AssemblySgdOutCreateRequest, 'fio'> & {
	fio: string | number;
};

export const IssueDetailsFormAssembly = ({ techMap, mappedEmployees }: IssueDetailsFormAssemblyProps) => {
	const [groupVisibility, setGroupVisibility] = useState(false);
	const [isFetching, setIsFetching] = useState(false);
	const currentUserId = useAppSelector(selectCurrentUserId);
	const { showOperationToast, showErrorToast } = useOperationNotification();
	const [updateAssemblyTechMap, { isSuccess }] = useUpdateAssemblyTechMapMutation();

	const { control, handleSubmit } = useForm<AssemblyFormValues>({
		resolver: yupResolver(issueDetailsSchema),
		defaultValues: {
			fio:
				mappedEmployees.find((employee) => Number(employee.value.split('_')[0]) === currentUserId)?.value ??
				undefined,
		},
	});

	const onSubmit = (data: AssemblyFormValues) => {
		if (!techMap?.id || !data.date || !data?.count || !techMap?.remaining_count) return;

		if (data?.count > techMap?.remaining_count) {
			showOperationToast({ status: 'warning', type: 'over_count', remainingCount: techMap.remaining_count });
			return;
		}
		setIsFetching(true);

		const date = formatDate(data?.date);
		const fio = mappedEmployees.find(
			(employee) => Number(employee.value.split('_')[0]) === Number(data.fio),
		)?.label;

		// Create the new assembly SGD out record
		const assemblySgdOutToCreate: AssemblySgdOutCreateRequest[] = [
			{
				date,
				count: data.count,
				receiver: data.receiver,
				certificate: data.certificate,
				fio,
			},
		];

		// Create the payload for the API
		const payload: BodyUpdateAssemblyTechMap = {
			assembly_sgd_out_payload: {
				assembly_sgd_out_to_create: assemblySgdOutToCreate,
				assembly_sgd_out_to_update: techMap.assembly_sgd_out,
			},
		};

		updateAssemblyTechMap({
			assemblyTechMapId: techMap.id,
			bodyUpdateAssemblyTechMap: payload,
		})
			.unwrap()
			.catch((err) => {
				if (err?.status) {
					switch (err.status) {
						case SGD_OUT_COUNT_TOO_LARGE:
							showErrorToast(
								'Не удалось выдать сборочные единицы',
								`Количество оставшихся сборочных единиц: ${techMap?.remaining_count}`,
							);
							break;
						default: {
							showErrorToast(
								'Не удалось сохранить карту',
								'При сохранении заготовительной МК произошла ошибка',
							);
						}
					}
				}
			})
			.finally(() => setIsFetching(false));
	};

	if (isSuccess) return <DetailsIssuedAlert productTechMapNumber={techMap?.number} />;

	return (
		<OperationLayout
			header={<OperationFormHeader techMap={techMap} variant="assembly" />}
			footer={
				<Button width="100%" onClick={handleSubmit(onSubmit)} isDisabled={isFetching}>
					Сохранить
				</Button>
			}
		>
			<chakra.div display="flex" flexDirection="column" gap="20px">
				<Controller
					name="date"
					control={control}
					render={({ field: { onChange } }) => (
						<CalendarSinglePicker
							clearable={false}
							defaultDate={dayjs().format('YYYY-MM-DD')}
							label="Дата"
							onSelectDate={(date) => onChange(date)}
						/>
					)}
				/>
				<Controller
					name="fio"
					control={control}
					render={({ field: { value, onChange }, fieldState: { error } }) => (
						<CustomSelect
							onFocus={() => setGroupVisibility(true)}
							onBlur={() => setGroupVisibility(false)}
							showSearch
							allowClear={false}
							options={mappedEmployees}
							value={value}
							defaultValue={mappedEmployees.find((employee) => employee.value === value)?.label}
							onChange={(e) => onChange(e)}
							filterOption={(input, option) =>
								String(option?.label ?? '')
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							optionFilterProp="children"
							dropdownStyle={{ zIndex: 1400 }}
							size="large"
							label="ФИО"
							className={clsx(
								styles['select'],
								styles['select-large'],
								groupVisibility && styles['select-focus'],
								error && [styles['select-invalid']],
							)}
						/>
					)}
				/>
				<Controller
					name="count"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<CustomInput
							type="number"
							size="md"
							{...field}
							isInvalid={!!error}
							label="Укажите количество"
							showTooltip={!!error?.message}
							tooltipContent={error?.message}
						/>
					)}
				/>
				<Controller
					name="receiver"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<CustomInput
							size="md"
							{...field}
							isInvalid={!!error}
							label="Укажите получателя"
							showTooltip={!!error?.message}
							tooltipContent={error?.message}
						/>
					)}
				/>
				<Controller
					name="certificate"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<CustomInput
							size="md"
							{...field}
							isInvalid={!!error}
							label="Укажите спецификацию"
							showTooltip={!!error?.message}
							tooltipContent={error?.message}
						/>
					)}
				/>
			</chakra.div>
		</OperationLayout>
	);
};
